import { For, Show, createSignal } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import toast from '../lib/toastStore.js';
import { qty } from '../lib/format.js';
import ThemedTable, { UserCell, StatusBeacon, RelTimeCell } from '../components/ThemedTable/index.js';
import './RelayOperations.css';

const time = value => value ? new Date(value).toLocaleString() : 'Never';

async function saveAccount(account, relayContributor, notes) {
  const response = await fetch(`/api/v2/profile-management/${encodeURIComponent(account.id)}`, {
    method: 'PATCH', credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ role: account.role, familyMember: account.familyMember, relayContributor, notes }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
}

export default function RelayOperations(props) {
  const inactiveDays = () => props.widget?.settings?.inactiveDays || 30;
  const { data, loading, error, refetch } = useWidgetData('relay-operations', () => ({ inactiveDays: inactiveDays() }));
  const [busy, setBusy] = createSignal(null);
  const summary = () => data()?.summary || {};

  async function updateAccount(account, event) {
    event.preventDefault();
    const form = event.currentTarget;
    setBusy(account.id);
    try {
      await saveAccount(account, form.relayContributor.checked, form.notes.value);
      toast.success('Relay account updated', { detail: account.displayName });
      await refetch();
    } catch (err) {
      toast.error('Relay account update failed', { detail: err.message });
    } finally { setBusy(null); }
  }

  const columns = [
    {
      key: 'account',
      label: 'Account',
      sortable: true,
      type: 'string',
      canHide: false,
      getValue: r => r.displayName,
      render: (_v, r) => (
        <div>
          <UserCell name={r.displayName} tag={r.atTag} />
          <Show when={r.role && r.role !== 'member'}>
            <span class="v2-tt-meta-badge" style={{ 'font-size': '10px', 'margin-left': '4px' }}>{r.role}</span>
          </Show>
          <div class="v2-tt-dim" style={{ 'font-size': '10px', 'margin-top': '2px' }}>
            {r.activeDevices}/{r.deviceCount} active devices
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      type: 'string',
      getValue: r => r.status,
      render: (v) => (
        <StatusBeacon
          status={v === 'active' ? 'online' : v === 'idle' ? 'warning' : 'offline'}
          label={v}
        />
      ),
    },
    {
      key: 'version',
      label: 'Client',
      sortable: true,
      type: 'string',
      getValue: r => r.clientVersion || '',
      render: (_v, r) => (
        <div>
          <span class="v2-tt-mono">v{r.clientVersion || '?'}</span>
          <div class="v2-tt-dim" style={{ 'font-size': '10px' }}>{r.platforms?.join(', ') || 'Platform unknown'}</div>
        </div>
      ),
    },
    {
      key: 'lastSync',
      label: 'Last Sync',
      sortable: true,
      type: 'time',
      align: 'right',
      getValue: r => r.lastSyncAt || r.lastSeenAt || 0,
      render: (_v, r) => <RelTimeCell ts={r.lastSyncAt || r.lastSeenAt} />,
    },
    {
      key: 'syncPoints',
      label: 'Points',
      sortable: true,
      type: 'number',
      align: 'right',
      getValue: r => r.syncPoints || 0,
      render: (_v, r) => (
        <div>
          <strong class="v2-tt-gold">{qty(r.syncPoints)}</strong>
          <div class="v2-tt-dim" style={{ 'font-size': '10px' }}>{qty(r.mmSyncs)} MM · {qty(r.ttcSyncs)} TTC</div>
        </div>
      ),
    },
    {
      key: 'sessionCount',
      label: 'Sessions',
      sortable: true,
      type: 'number',
      align: 'right',
      getValue: r => r.sessionCount || 0,
      render: (_v, r) => (
        <div>
          <span class="v2-tt-mono">{qty(r.sessionCount)}</span>
          <div class="v2-tt-dim" style={{ 'font-size': '10px' }}>{qty(r.sales)} sales</div>
        </div>
      ),
    },
    {
      key: 'controls',
      label: 'Controls',
      sortable: false,
      render: (_v, r) => (
        <form
          class="v2-relay-ops__controls"
          style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}
          onSubmit={event => updateAccount(r, event)}
        >
          <label style={{ 'font-size': '11px', display: 'flex', 'align-items': 'center', gap: '3px' }}>
            <input name="relayContributor" type="checkbox" checked={r.relayContributor} /> Contrib
          </label>
          <input
            name="notes"
            class="form-input"
            value={r.notes || ''}
            maxlength="2000"
            placeholder="Admin note…"
            style={{ 'font-size': '11px', padding: '2px 5px', width: '100px' }}
          />
          <button
            class="btn"
            type="submit"
            disabled={busy() === r.id}
            style={{ 'font-size': '11px', padding: '2px 8px' }}
          >
            {busy() === r.id ? '…' : 'Save'}
          </button>
        </form>
      ),
    },
  ];

  return (
    <div class="v2-widget v2-relay-ops">
      <header class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">↯ Relay Operations & Client Mesh</span>
        <button class="v2-widget-hdr__btn" onClick={() => refetch()} title="Refresh relay data">↻</button>
      </header>
      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading relay operations…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <div class="v2-relay-ops__body">
            <Show when={data()?.syncStatus}>
              <div class="v2-kpi-strip" style={{ 'margin-bottom': '8px' }}>
                <div class="v2-kpi-card">
                  <span class="v2-kpi-card__label">MM Sync Status</span>
                  <strong class="v2-kpi-card__value">
                    {data().syncStatus.mm?.finishedAt ? time(data().syncStatus.mm.finishedAt) : '—'}
                  </strong>
                  <span class="v2-kpi-card__sub">
                    {qty(data().syncStatus.mm?.totalSales || 0)} sales · {data().syncStatus.isStale ? '⚠️ Stale' : '✓ Fresh'}
                  </span>
                </div>
                <div class="v2-kpi-card">
                  <span class="v2-kpi-card__label">TTC Price Table</span>
                  <strong class="v2-kpi-card__value">
                    {data().syncStatus.ttc?.finishedAt ? time(data().syncStatus.ttc.finishedAt) : '—'}
                  </strong>
                  <span class="v2-kpi-card__sub">
                    {qty(data().syncStatus.ttc?.totalItems || 0)} items indexed
                  </span>
                </div>
                <div class="v2-kpi-card">
                  <span class="v2-kpi-card__label">Raffle Sync</span>
                  <strong class="v2-kpi-card__value">
                    {data().syncStatus.raffle?.finishedAt ? time(data().syncStatus.raffle.finishedAt) : '—'}
                  </strong>
                  <span class="v2-kpi-card__sub">
                    {qty(data().syncStatus.raffle?.totalEntries || 0)} entries
                  </span>
                </div>
              </div>
            </Show>
            <section class="v2-relay-ops__summary">
              <For each={[
                ['Active sessions', summary().activeSessions || 0],
                ['Active accounts', summary().activeAccounts || 0],
                ['Inactive', summary().inactiveAccounts || 0],
                ['Downloaded', summary().downloaded || 0],
                ['Update ready', summary().updated || 0],
                ['Sync failures', summary().syncFailed || 0],
              ]}>{item => <div><span>{item[0]}</span><b>{item[1]}</b></div>}</For>
            </section>

            <div style={{ flex: 1, 'min-height': 0, 'margin-top': '8px' }}>
              <ThemedTable
                columns={columns}
                rows={data()?.accounts || []}
                storageKey="relay_operations"
                defaultSortKey="syncPoints"
                defaultSortDir="desc"
                searchPlaceholder="Search relay account or version…"
                emptyText="No relay accounts found."
              />
            </div>

            <section class="v2-relay-ops__events" style={{ 'margin-top': '10px' }}>
              <h3>Recent relay events</h3>
              <div><For each={(data()?.recentEvents || []).slice(0, 20)}>{event => (
                <p>
                  <b>{event.event_type === 'updated' ? 'update ready' : event.event_type.replaceAll('_', ' ')}</b>
                  <span>{event.client_version ? `v${event.client_version}` : 'anonymous'}</span>
                  <span>{event.filename || event.detail || 'relay'}</span>
                  <time>{time(event.created_at)}</time>
                </p>
              )}</For></div>
            </section>
          </div>
        </Show>
      </Show>
    </div>
  );
}